import { Button, Drawer, Flex } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';

import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import {
	useTranslationCommonUi,
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import {
	ExpectedTransactionType,
	InvestmentDetailsAllocation,
	UpdateInvestmentAllocationDto,
} from '@dealstack/shared/lib/generated';

import { useMipSecurities } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useMipSecurities.ts';

import {
	ALLOCATION_FORM_FIELD,
	AllocationFormValues,
	AllocationSubmitValues,
	buildAllocationFormSchema,
	formatSecurityLabel,
} from './allocationForm';
import { useVestingTermsForDrawer } from './useVestingTermsForDrawer';
import { NumberInput, Select } from 'react-hook-form-mantine';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useMutation } from '@tanstack/react-query';
import { updateInvestmentAllocation } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useInvestmentDetailsAllocations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsAllocations.ts';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	allocation: InvestmentDetailsAllocation | null;
	existingAllocations: InvestmentDetailsAllocation[];
	onDeleteAllocation: (allocation: InvestmentDetailsAllocation | null) => void;
}

const allocationToFormValues = (a: InvestmentDetailsAllocation): AllocationFormValues => ({
	securityId: a.securityId,
	units: a.units,
	cost: a.cost,
	vestingTermId: a.vestingTermId,
	expectedTransactionType: a.transactionType,
});

const moneyChanged = (a: number, b: number) => Math.abs(a - b) >= 0.005;

const buildUpdatePayload = (
	current: InvestmentDetailsAllocation,
	next: AllocationSubmitValues
): UpdateInvestmentAllocationDto => ({
	securityId: next.securityId !== current.securityId ? next.securityId : undefined,
	units: next.units !== current.units ? next.units : undefined,
	cost: moneyChanged(next.cost, current.cost) ? next.cost : undefined,
	vestingTermId: next.vestingTermId !== current.vestingTermId ? next.vestingTermId : undefined,
	expectedTransactionType:
		next.expectedTransactionType !== current.transactionType
			? next.expectedTransactionType
			: undefined,
});

export const EditInvestmentAllocationDrawer = ({
	opened,
	onClose,
	allocation,
	existingAllocations,
	onDeleteAllocation,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const tDefault = useTranslationDefault();
	const { data: securities = [] } = useMipSecurities();
	const vestingTermOptions = useVestingTermsForDrawer();
	const { mipId, investmentId } = useRequiredParams('mipId', 'investmentId');
	const updateAllocationRequest = useMutation({ mutationFn: updateInvestmentAllocation });
	const { refetch: refetchAllocations } = useInvestmentDetailsAllocations();
	const { refetch: refetchSummary } = useInvestmentDetailsSummary();

	const schema = useMemo(() => buildAllocationFormSchema(tCommonUi), [tCommonUi]);

	const form = useForm<AllocationFormValues, unknown, AllocationSubmitValues>({
		resolver: zodResolver(schema),
		defaultValues: allocation ? allocationToFormValues(allocation) : undefined,
	});

	useEffect(() => {
		if (opened && allocation) {
			form.reset(allocationToFormValues(allocation));
		}
	}, [opened, allocation, form]);

	const securityOptions = useMemo(() => {
		if (!allocation) {
			return [];
		}

		const allocatedElsewhereIds = new Set(
			existingAllocations.filter((a) => a.id !== allocation.id).map((a) => a.securityId)
		);
		return securities
			.filter(
				(s) =>
					!s.deleted &&
					(s.id === allocation.securityId || !allocatedElsewhereIds.has(s.id))
			)
			.map((s) => ({ value: s.id, label: formatSecurityLabel(s) }));
	}, [securities, existingAllocations, allocation]);

	const onSubmit = async (values: AllocationSubmitValues) => {
		if (!allocation) {
			return;
		}

		const payload = buildUpdatePayload(allocation, values);

		updateAllocationRequest.mutate(
			{ mipId, investmentId, allocationId: allocation.id, payload },
			{
				onSuccess: () => {
					void refetchAllocations();
					void refetchSummary();

					showToastV2({
						title: tMipManage('investment_allocations.drawer.update_success'),
						state: 'success',
					});
					onClose();
				},
				onError: (err) => {
					showToastV2({
						title: tMipManage('investment_allocations.drawer.error'),
						message: isStandardBackendError(err)
							? tDefault(`error_codes.${err.error?.code}`)
							: undefined,
						state: 'error',
					});
				},
			}
		);
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('investment_allocations.drawer.edit_title')}
		>
			<FormProvider {...form}>
				<form style={{ height: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
					<Flex direction="column" h="100%" gap="1rem">
						<Select
							name={ALLOCATION_FORM_FIELD.securityId}
							control={form.control}
							label={tMipManage('investment_allocations.drawer.security_label')}
							placeholder={tMipManage(
								'investment_allocations.drawer.security_placeholder'
							)}
							size="md"
							data={securityOptions}
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							searchable
						/>

						<NumberInput
							name={ALLOCATION_FORM_FIELD.units}
							control={form.control}
							label={tMipManage('investment_allocations.drawer.units_label')}
							placeholder={tMipManage(
								'investment_allocations.drawer.units_placeholder'
							)}
							size="md"
							hideControls
							min={0}
						/>

						<NumberInput
							name={ALLOCATION_FORM_FIELD.cost}
							control={form.control}
							label={tMipManage('investment_allocations.drawer.total_cost_label')}
							placeholder={tMipManage(
								'investment_allocations.drawer.total_cost_placeholder'
							)}
							size="md"
							hideControls
							min={0}
							decimalScale={2}
						/>

						<Select
							name={ALLOCATION_FORM_FIELD.expectedTransactionType}
							control={form.control}
							label={tMipManage(
								'investment_allocations.drawer.transaction_type_label'
							)}
							placeholder={tMipManage(
								'investment_allocations.drawer.transaction_type_placeholder'
							)}
							size="md"
							data={Object.values(ExpectedTransactionType).map((value) => ({
								value,
								label: value,
							}))}
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							clearable
						/>

						<Select
							name={ALLOCATION_FORM_FIELD.vestingTermId}
							control={form.control}
							label={tMipManage('investment_allocations.drawer.vesting_term_label')}
							placeholder={tMipManage(
								'investment_allocations.drawer.vesting_term_placeholder'
							)}
							size="md"
							data={vestingTermOptions}
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							clearable
						/>

						<Button
							fullWidth
							mt="auto"
							type="submit"
							loading={updateAllocationRequest.isPending}
							fw={600}
						>
							{tMipManage('investment_allocations.drawer.edit_save_cta')}
						</Button>

						<Button
							variant="subtle"
							color="red"
							fullWidth
							fw={600}
							onClick={() => onDeleteAllocation(allocation)}
						>
							{tMipManage('investment_allocations.drawer.remove_cta')}
						</Button>
					</Flex>
				</form>
			</FormProvider>
		</Drawer>
	);
};
